import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Disclosure, FormCheckbox } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog.tsx';
import BacktestOperationsToolbar from './components/BacktestOperationsToolbar';
import BacktestStatusBanners from './components/BacktestStatusBanners';
import BacktestHealthStrip from './components/BacktestHealthStrip';
import MissingEntitiesPanel from './components/MissingEntitiesPanel';
import BacktestBreakdownPanel from './components/BacktestBreakdownPanel';
import QueueActivityPanel from './components/QueueActivityPanel';
import BacktestDiagnostics from './components/BacktestDiagnostics';
import BacktestDangerZone from './components/BacktestDangerZone';
import { formatSecondsAgo, formatStrategyVersionKey } from './components/backtestStatusFormatters';
import PopulationPriorityInput from './components/PopulationPriorityInput';
import PreviousVersionBacktestsNotice from './components/PreviousVersionBacktestsNotice';
import { ACTIVE_COVERAGE_POLL_INTERVAL_MS, getActiveQueueJobCount, getQueuePollInterval } from './strategyBacktestPolling';
import type { PreviousVersionBacktestCleanupPreview } from './components/backtestStatusTypes';

type ResetPreview = {
    toDeleteCount: number;
    retainedBacktestsCount: number;
    sampleRetainedBacktestIds: number[];
};

type PopulationPreview = {
    estimatedJobCount: number;
    plan?: {
        entityCount: number;
        exitCandidates: { candidateId: number; exitFingerprint: string }[];
        optionBuyingStrategyConfigurations: {
            leg1?: number | null;
            leg2?: number | null;
            leg3?: number | null;
            leg4?: number | null;
            leg5?: number | null;
            leg6?: number | null;
        }[];
        requiresLargeBatchAcknowledgement: boolean;
        reusedBacktestIds: number[];
        completedEmptyJobIds: number[];
        pendingJobIds: number[];
    };
};

interface StrategyBacktestOperationsProps {
    strategyId: number;
    /** False for shared strategies: coverage stays readable, destructive and queueing actions do not. */
    canManage: boolean;
}

/**
 * Coverage, queue state and backtest operations for one strategy. Formerly the standalone
 * `/tools/backtestStatus` page, which was strategy-scoped in every query but made you pick the
 * strategy from a dropdown after navigating away from it.
 */
const StrategyBacktestOperations = ({ strategyId, canManage }: StrategyBacktestOperationsProps) => {
    const [selectedEntityType, setSelectedEntityType] = useState<'' | EntityTypeUnion['entityType']>('');
    const [isGlobalPopulateConfirmOpen, setIsGlobalPopulateConfirmOpen] = useState(false);
    const [globalPopulationPreview, setGlobalPopulationPreview] = useState<PopulationPreview | null>(null);
    const [isDeleteBacktestsOpen, setIsDeleteBacktestsOpen] = useState(false);
    const [deletePreview, setDeletePreview] = useState<ResetPreview | null>(null);
    const [shouldCancelQueueJobsOnReset, setShouldCancelQueueJobsOnReset] = useState<boolean>(true);
    const [shouldPurgeQueueHistoryOnReset, setShouldPurgeQueueHistoryOnReset] = useState<boolean>(false);
    const [shouldRepopulateOnReset, setShouldRepopulateOnReset] = useState<boolean>(true);
    const [isPreviousVersionCleanupOpen, setIsPreviousVersionCleanupOpen] = useState(false);
    const [previousVersionCleanupPreview, setPreviousVersionCleanupPreview] = useState<PreviousVersionBacktestCleanupPreview | null>(null);
    const [nowTick, setNowTick] = useState<number>(() => getUnixTime());
    const [isMissingExpanded, setIsMissingExpanded] = useState<boolean>(false);
    const pollingScopeKeyRef = useRef<string>('');
    const hasObservedActiveQueueRef = useRef<boolean>(false);

    const [entityToPopulate, setEntityToPopulate] = useState<{
        id: number;
        type: EntityTypeUnion['entityType'];
        symbol: string;
    } | null>(null);
    const [entityPopulationPreview, setEntityPopulationPreview] = useState<PopulationPreview | null>(null);

    // Cache bypass - when true, forces fresh entry calculation instead of using cached entries
    // Default to true since when re-populating after strategy changes, you typically want fresh entries
    const [bypassCache, setBypassCache] = useState<boolean>(true);
    const [populationPriority, setPopulationPriority] = useState<number>(5);

    // Keep the "last updated" label ticking without forcing a network refetch.
    useEffect(() => {
        const interval = window.setInterval(() => setNowTick(getUnixTime()), 10_000);

        return () => window.clearInterval(interval);
    }, []);

    // The idle heartbeat detects jobs started by another page or scheduled process. Once work is
    // visible, queue polling accelerates and coverage polling starts automatically.
    const queueStatusQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyQueueStatus.queryOptions({
                strategyId,
                entityType: selectedEntityType || null,
                strategyVersionKeyMode: 'current'
            }),
            enabled: true,
            refetchInterval: (query) => getQueuePollInterval(query.state.data)
        },
        {
            fallbackErrorMessage: 'Failed to fetch strategy queue status',
            showErrorToast: true
        }
    );

    const activeQueueJobCount = getActiveQueueJobCount(queueStatusQuery.data);
    const hasActiveQueueJobs = activeQueueJobCount > 0;

    const scorecardContextQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyScorecardContext.queryOptions({
                strategyId
            }),
            enabled: true
        },
        {
            fallbackErrorMessage: 'Failed to fetch strategy scorecard info',
            showErrorToast: true
        }
    );

    const coverageQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyCoverage.queryOptions({
                strategyId,
                entityType: selectedEntityType || null,
                strategyVersionKeyMode: 'current'
            }),
            enabled: true,
            refetchInterval: hasActiveQueueJobs ? ACTIVE_COVERAGE_POLL_INTERVAL_MS : false
        },
        {
            fallbackErrorMessage: 'Failed to fetch strategy coverage',
            showErrorToast: true
        }
    );

    const analysisQuery = useAppQuery(
        {
            ...trpc.backtest.analyzeParameterCombinations.queryOptions({
                strategyId,
                entityType: selectedEntityType || null,
                keyAssetsOnly: false,
                strategyVersionKeyMode: 'current'
            }),
            enabled: true
        },
        {
            fallbackErrorMessage: 'Failed to analyze backtest parameter combinations',
            showErrorToast: false
        }
    );

    const optionBuyingStrategiesQuery = useAppQuery(
        {
            ...trpc.strategy.fetchOptionBuyingStrategies.queryOptions({}),
            enabled: true
        },
        {
            fallbackErrorMessage: 'Failed to load option buying strategy names',
            showErrorToast: false
        }
    );

    const resetPreviewQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyResetPreview.queryOptions({
                strategyId,
                entityType: selectedEntityType || null
            }),
            enabled: false
        },
        {
            fallbackErrorMessage: 'Failed to preview strategy reset',
            showErrorToast: true
        }
    );

    const previousVersionCleanupPreviewQuery = useAppQuery(
        {
            ...trpc.backtest.previewPreviousVersionBacktestCleanup.queryOptions({
                strategyId,
                entityType: selectedEntityType || null
            }),
            enabled: false
        },
        {
            fallbackErrorMessage: 'Failed to preview previous-version cleanup',
            showErrorToast: true
        }
    );

    const triggerPopulateMutation = useTrpcMutation(
        trpc.backtest.triggerPopulateForStrategy.mutationOptions({
            onSuccess: () => {
                void coverageQuery.refetch();
                void queueStatusQuery.refetch();
            }
        }),
        {
            successMessage: 'Backtest populate request submitted',
            fallbackErrorMessage: 'Failed to trigger backtest populate'
        }
    );

    const previewPopulateMutation = useTrpcMutation(trpc.backtest.previewPopulateForStrategy.mutationOptions(), {
        fallbackErrorMessage: 'Failed to preview backtest population'
    });

    const raisePopulationPriorityMutation = useTrpcMutation(
        trpc.backtest.raiseStrategyPopulationQueuePriority.mutationOptions({
            onSuccess: (result) => {
                if (!result.success) return;

                void queueStatusQuery.refetch();
                showToast(
                    result.updatedJobs > 0
                        ? `Raised ${result.updatedJobs} population job${result.updatedJobs === 1 ? '' : 's'} to priority ${result.priority}`
                        : `No eligible population jobs were below priority ${result.priority}`,
                    result.updatedJobs > 0 ? 'success' : 'info'
                );
            }
        }),
        {
            fallbackErrorMessage: 'Failed to raise population queue priority'
        }
    );

    const resetStrategyBacktestsMutation = useTrpcMutation(
        trpc.backtest.resetStrategyBacktests.mutationOptions({
            onSuccess: () => {
                void coverageQuery.refetch();
                void queueStatusQuery.refetch();
                void scorecardContextQuery.refetch();
            }
        }),
        {
            fallbackErrorMessage: 'Failed to reset strategy backtests'
        }
    );

    const deletePreviousVersionBacktestsMutation = useTrpcMutation(
        trpc.backtest.deletePreviousVersionBacktests.mutationOptions({
            onSuccess: () => {
                void coverageQuery.refetch();
                void queueStatusQuery.refetch();
                void analysisQuery.refetch();
                void scorecardContextQuery.refetch();
            }
        }),
        {
            fallbackErrorMessage: 'Failed to delete previous-version backtests'
        }
    );

    const pollingScopeKey = `${strategyId}:${selectedEntityType || 'all'}:current`;

    // When the final active job leaves the queue, refresh the data that does not need continuous
    // polling so the completed state is coherent across the whole panel.
    useEffect(() => {
        if (queueStatusQuery.data === undefined) return;

        if (pollingScopeKeyRef.current !== pollingScopeKey) {
            pollingScopeKeyRef.current = pollingScopeKey;
            hasObservedActiveQueueRef.current = hasActiveQueueJobs;
            return;
        }

        if (hasActiveQueueJobs) {
            hasObservedActiveQueueRef.current = true;
            return;
        }

        if (!hasObservedActiveQueueRef.current) return;

        hasObservedActiveQueueRef.current = false;
        void Promise.all([coverageQuery.refetch(), analysisQuery.refetch(), scorecardContextQuery.refetch()]);
    }, [
        analysisQuery.refetch,
        coverageQuery.refetch,
        hasActiveQueueJobs,
        pollingScopeKey,
        queueStatusQuery.data,
        scorecardContextQuery.refetch
    ]);

    const handlePreviewPopulate = async () => {
        try {
            const result = await previewPopulateMutation.mutateAsync({
                strategyId,
                entityType: selectedEntityType || null,
                entityId: null,
                bypassCache,
                priority: populationPriority
            });

            if (!result.success) {
                showToast(result.errorMessages?.join(', ') || 'Failed to preview backtest jobs', 'error');
                return;
            }

            setGlobalPopulationPreview(result);
            setIsGlobalPopulateConfirmOpen(true);
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handleTriggerPopulate = async () => {
        if (!globalPopulationPreview) return;

        try {
            const result = await triggerPopulateMutation.mutateAsync({
                strategyId,
                entityType: selectedEntityType || null,
                entityId: null,
                bypassCache,
                priority: populationPriority,
                expectedJobCount: globalPopulationPreview.estimatedJobCount,
                acknowledgeLargeBatch: globalPopulationPreview.plan?.requiresLargeBatchAcknowledgement ?? false
            });

            if (result.success) {
                showToast(
                    `Submitted ${result.submittedJobs} backtest job${
                        result.submittedJobs === 1 ? '' : 's'
                    } at priority ${populationPriority} (confirmed total ${result.estimatedJobCount})`,
                    'success'
                );
            } else {
                showToast(result.errorMessages?.join(', ') || 'Failed to submit backtest jobs', 'error');
            }
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handlePreviewPopulateForEntity = async (entity: { id: number; type: EntityTypeUnion['entityType']; symbol: string }) => {
        try {
            const result = await previewPopulateMutation.mutateAsync({
                strategyId,
                entityType: entity.type,
                entityId: entity.id,
                bypassCache,
                priority: populationPriority
            });

            if (!result.success) {
                showToast(result.errorMessages?.join(', ') || 'Failed to preview entity backtests', 'error');
                return;
            }

            setEntityPopulationPreview(result);
            setEntityToPopulate(entity);
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handleTriggerPopulateForEntity = async (entityId: number, entityType: EntityTypeUnion['entityType']) => {
        if (!entityPopulationPreview) return;

        try {
            const result = await triggerPopulateMutation.mutateAsync({
                strategyId,
                entityType,
                entityId,
                bypassCache,
                priority: populationPriority,
                expectedJobCount: entityPopulationPreview.estimatedJobCount,
                acknowledgeLargeBatch: entityPopulationPreview.plan?.requiresLargeBatchAcknowledgement ?? false
            });

            if (result.success) {
                showToast(
                    `Submitted ${result.submittedJobs} backtest job${
                        result.submittedJobs === 1 ? '' : 's'
                    } for entity ${entityId} at priority ${populationPriority}`,
                    'success'
                );
            } else {
                showToast(result.errorMessages?.join(', ') || 'Failed to submit backtest jobs', 'error');
            }
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handleRaisePopulationPriority = async () => {
        try {
            await raisePopulationPriorityMutation.mutateAsync({
                strategyId,
                entityType: selectedEntityType || null,
                entityId: null,
                strategyVersionKeyMode: 'current',
                priority: populationPriority
            });
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handlePreviewPreviousVersionCleanup = async () => {
        try {
            const refreshed = await previousVersionCleanupPreviewQuery.refetch();

            if (!refreshed.data) {
                showToast('Failed to preview previous-version cleanup', 'error');
                return;
            }

            setPreviousVersionCleanupPreview(refreshed.data);
            setIsPreviousVersionCleanupOpen(true);
        } catch {
            // Toasts handled by previousVersionCleanupPreviewQuery error effect.
        }
    };

    const handleConfirmPreviousVersionCleanup = async () => {
        if (!previousVersionCleanupPreview) return;

        try {
            const result = await deletePreviousVersionBacktestsMutation.mutateAsync({
                strategyId,
                entityType:
                    previousVersionCleanupPreview.entityType === null
                        ? null
                        : requireOrdinaryEntityType(previousVersionCleanupPreview.entityType),
                expectedCurrentStrategyVersionKey: previousVersionCleanupPreview.currentStrategyVersionKey
            });

            const protectedSummary =
                result.protectedBacktestsCount > 0
                    ? ` ${result.protectedBacktestsCount} backtest${result.protectedBacktestsCount === 1 ? '' : 's'} remain protected.`
                    : '';

            showToast(
                `Deleted ${result.deletedBacktestsCount} previous-version backtest${
                    result.deletedBacktestsCount === 1 ? '' : 's'
                } and cancelled ${result.cancelledQueueJobsCount} previous-version queue job${
                    result.cancelledQueueJobsCount === 1 ? '' : 's'
                }.${protectedSummary}`,
                'success'
            );
            setIsPreviousVersionCleanupOpen(false);
            setPreviousVersionCleanupPreview(null);
        } catch {
            // Toast handled by useTrpcMutation. Keep the preview open so the user can review the failure.
        }
    };

    const handleResetBacktestsPreview = async () => {
        try {
            const refreshed = await resetPreviewQuery.refetch();

            if (!refreshed.data) {
                showToast('Failed to preview strategy reset', 'error');
                return;
            }

            setDeletePreview(refreshed.data);
            setIsDeleteBacktestsOpen(true);
        } catch {
            // Toasts handled by resetPreviewQuery error effect.
        }
    };

    const handleConfirmResetBacktests = async () => {
        try {
            const resetParams = {
                strategyId,
                entityType: selectedEntityType || null,
                cancelQueueJobs: shouldCancelQueueJobsOnReset,
                purgeQueueHistory: shouldCancelQueueJobsOnReset ? shouldPurgeQueueHistoryOnReset : false,
                repopulate: shouldRepopulateOnReset,
                bypassCache,
                priority: populationPriority
            };

            const result = await resetStrategyBacktestsMutation.mutateAsync(resetParams);

            if (result.success) {
                const sourceReferenceSummary = ` Retained ${result.retainedBacktestsCount} protected backtests and their evidence.`;

                const repopulateSummary = result.repopulateStarted
                    ? ' Repopulation started in the background; refresh queue status shortly.'
                    : '';

                showToast(
                    `Deleted ${result.deletedBacktestsCount} backtest${
                        result.deletedBacktestsCount === 1 ? '' : 's'
                    }. Cancelled ${result.cancelledQueueJobsCount} queued job${
                        result.cancelledQueueJobsCount === 1 ? '' : 's'
                    }.${sourceReferenceSummary}${repopulateSummary}`,
                    'success'
                );

                if (result.repopulateStarted) {
                    window.setTimeout(() => {
                        queueStatusQuery.refetch().catch(() => undefined);
                    }, 2500);
                }
            }

            setDeletePreview(null);
        } catch {
            // Toasts handled by useTrpcMutation
        } finally {
            setIsDeleteBacktestsOpen(false);
        }
    };

    const scrollToMissingEntities = () => {
        setIsMissingExpanded(true);
        document.getElementById('backtest-missing-entities')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const isLoadingCoverage = coverageQuery.isLoading && true;
    const isLoadingQueue = queueStatusQuery.isLoading && true;
    const isRefreshing = coverageQuery.isFetching || queueStatusQuery.isFetching;

    const coverage = coverageQuery.data;
    const queueStatus = queueStatusQuery.data;
    const analysis = analysisQuery.data;
    const isLoadingAnalysis = analysisQuery.isLoading && true;

    const exitCandidateLabelById = useMemo(() => {
        const map = new Map<number, string>();

        for (const candidate of coverage?.configuredExitCandidates ?? []) {
            map.set(candidate.candidateId, candidate.label);
        }

        return map;
    }, [coverage]);

    const optionStrategyNameById = useMemo(() => {
        const map = new Map<number, string>();

        for (const strategy of optionBuyingStrategiesQuery.data?.strategies ?? []) {
            if (strategy.name) map.set(strategy.id, strategy.name);
        }

        return map;
    }, [optionBuyingStrategiesQuery.data]);

    const lastUpdatedLabel = coverageQuery.dataUpdatedAt
        ? formatSecondsAgo(Math.max(0, nowTick - Math.floor(coverageQuery.dataUpdatedAt / 1000)))
        : null;

    return (
        <div className="flex flex-col gap-4">
            <BacktestOperationsToolbar
                activeQueueJobCount={activeQueueJobCount}
                canManage={canManage}
                isPopulating={triggerPopulateMutation.isPending || previewPopulateMutation.isPending}
                isRefreshing={isRefreshing}
                lastUpdatedLabel={lastUpdatedLabel}
                selectedEntityType={selectedEntityType}
                onEntityTypeChange={setSelectedEntityType}
                onPopulateMissing={() => {
                    void handlePreviewPopulate();
                }}
                onRefresh={() => {
                    void coverageQuery.refetch();
                    void queueStatusQuery.refetch();
                    void scorecardContextQuery.refetch();
                    void analysisQuery.refetch();
                }}
            />

            {(isLoadingCoverage || isLoadingQueue) && (
                <Card>
                    <DataLoadingSpinner text="Loading backtest status..." />
                </Card>
            )}

            {coverage && (
                <>
                    <BacktestStatusBanners coverage={coverage} queueStatus={queueStatus} scorecardContext={scorecardContextQuery.data} />

                    <BacktestHealthStrip
                        coverage={coverage}
                        queueStatus={queueStatus}
                        scorecardContext={scorecardContextQuery.data}
                        onViewMissing={scrollToMissingEntities}
                    />

                    <QueueActivityPanel
                        isRaisingPriority={raisePopulationPriorityMutation.isPending}
                        populationPriority={populationPriority}
                        queueStatus={queueStatus}
                        onPopulationPriorityChange={setPopulationPriority}
                        onRaisePriority={() => {
                            void handleRaisePopulationPriority();
                        }}
                    />

                    <div id="backtest-missing-entities">
                        <MissingEntitiesPanel
                            canPopulate={canManage}
                            expanded={isMissingExpanded}
                            isPopulating={triggerPopulateMutation.isPending || previewPopulateMutation.isPending}
                            missingEntities={coverage.missingEntities}
                            missingEntitiesCount={coverage.missingEntitiesCount}
                            onPopulateAll={() => {
                                void handlePreviewPopulate();
                            }}
                            onPopulateEntity={(entity) => {
                                void handlePreviewPopulateForEntity({ ...entity, type: requireOrdinaryEntityType(entity.type) });
                            }}
                            onToggleExpanded={setIsMissingExpanded}
                        />
                    </div>

                    <PreviousVersionBacktestsNotice
                        canManage={canManage}
                        coverage={coverage}
                        isLoading={previousVersionCleanupPreviewQuery.isFetching}
                        onReviewCleanup={() => {
                            void handlePreviewPreviousVersionCleanup();
                        }}
                    />

                    {/* Collapsed by default: both are investigation surfaces, not part of the routine
                        "is this strategy covered?" read that the health strip already answers. */}
                    <Disclosure title="Current-version performance by parameter combination">
                        <BacktestBreakdownPanel
                            analysis={analysis}
                            exitCandidateLabelById={exitCandidateLabelById}
                            isLoading={isLoadingAnalysis}
                            optionStrategyNameById={optionStrategyNameById}
                            populationProgress={coverage.populationProgress}
                            strategyId={strategyId}
                        />
                    </Disclosure>

                    <Disclosure title="Diagnostics">
                        <BacktestDiagnostics coverage={coverage} queueStatus={queueStatus} scorecardContext={scorecardContextQuery.data} />
                    </Disclosure>

                    {canManage && (
                        <BacktestDangerZone
                            disabled={!canManage}
                            isBusy={resetStrategyBacktestsMutation.isPending || resetPreviewQuery.isFetching}
                            onReset={handleResetBacktestsPreview}
                        />
                    )}
                </>
            )}

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Submit populate jobs"
                confirmVariant="primary"
                isConfirmLoading={triggerPopulateMutation.isPending}
                isOpen={isGlobalPopulateConfirmOpen}
                message={
                    <div className="space-y-3 text-sm">
                        <p>
                            This will submit populate jobs for every missing backtest combination on this strategy
                            {selectedEntityType ? ` and entity type ${selectedEntityType}` : ''}.
                        </p>
                        {globalPopulationPreview?.plan && (
                            <>
                                <p>
                                    Exact plan: <strong>{globalPopulationPreview.estimatedJobCount}</strong> job
                                    {globalPopulationPreview.estimatedJobCount === 1 ? '' : 's'} across{' '}
                                    <strong>{globalPopulationPreview.plan.entityCount}</strong> entities,{' '}
                                    <strong>{globalPopulationPreview.plan.exitCandidates.length}</strong> exit
                                    {globalPopulationPreview.plan.exitCandidates.length === 1 ? '' : 's'}, and{' '}
                                    <strong>{globalPopulationPreview.plan.optionBuyingStrategyConfigurations.length}</strong> option
                                    configuration
                                    {globalPopulationPreview.plan.optionBuyingStrategyConfigurations.length === 1 ? '' : 's'}.
                                </p>
                                {(globalPopulationPreview.plan.reusedBacktestIds.length > 0 ||
                                    globalPopulationPreview.plan.completedEmptyJobIds.length > 0 ||
                                    globalPopulationPreview.plan.pendingJobIds.length > 0) && (
                                    <p>
                                        Existing coverage: <strong>{globalPopulationPreview.plan.reusedBacktestIds.length}</strong> saved,{' '}
                                        <strong>{globalPopulationPreview.plan.completedEmptyJobIds.length}</strong> completed with no
                                        entries, and <strong>{globalPopulationPreview.plan.pendingJobIds.length}</strong> queued or running.
                                    </p>
                                )}
                            </>
                        )}
                        <div className="space-y-3 border-t border-zinc-600 pt-3">
                            <PopulationPriorityInput
                                id="populationPriorityGlobal"
                                priority={populationPriority}
                                onChange={setPopulationPriority}
                            />
                            <FormCheckbox
                                checked={bypassCache}
                                helpText="Forces fresh entry calculation. Enable this after making changes to strategy entry logic to avoid using stale cached entries."
                                id="bypassCacheGlobal"
                                label="Bypass entry cache"
                                onChange={(e) => setBypassCache(e.target.checked)}
                            />
                        </div>
                        {globalPopulationPreview?.plan?.requiresLargeBatchAcknowledgement && (
                            <p className="text-red-400">
                                This exceeds the large-batch safety threshold. Confirming explicitly acknowledges the displayed job count.
                            </p>
                        )}
                    </div>
                }
                title="Populate backtests for strategy"
                onCancel={() => {
                    setIsGlobalPopulateConfirmOpen(false);
                    setGlobalPopulationPreview(null);
                }}
                onConfirm={() => {
                    setIsGlobalPopulateConfirmOpen(false);
                    void handleTriggerPopulate();
                    setGlobalPopulationPreview(null);
                }}
            />

            <ConfirmationDialog
                cancelText="Keep previous versions"
                confirmText="Delete previous versions"
                confirmVariant="danger"
                isConfirmLoading={deletePreviousVersionBacktestsMutation.isPending}
                isOpen={isPreviousVersionCleanupOpen}
                message={
                    previousVersionCleanupPreview ? (
                        <div className="space-y-3 text-sm">
                            <p>
                                This will cancel <strong>{previousVersionCleanupPreview.activeQueueJobsCount}</strong> active
                                previous-version job{previousVersionCleanupPreview.activeQueueJobsCount === 1 ? '' : 's'} and delete{' '}
                                <strong>{previousVersionCleanupPreview.deletableBacktestsCount}</strong> safely deletable backtest
                                {previousVersionCleanupPreview.deletableBacktestsCount === 1 ? '' : 's'}.
                            </p>
                            <div className="max-h-48 space-y-1 overflow-y-auto rounded border border-zinc-700 bg-zinc-900/60 p-3 text-xs">
                                {previousVersionCleanupPreview.previousVersions.map((version) => (
                                    <div
                                        key={version.strategyVersionKey ?? 'legacy-null'}
                                        className="flex justify-between gap-4 text-zinc-300"
                                    >
                                        <span className="font-mono">{formatStrategyVersionKey(version.strategyVersionKey)}</span>
                                        <span>
                                            {version.backtestCount.toLocaleString()} backtest{version.backtestCount === 1 ? '' : 's'}
                                            {version.lastUpdateDateTime ? ` · ${version.lastUpdateDateTime}` : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {previousVersionCleanupPreview.protectedBacktestsCount > 0 && (
                                <p className="rounded border border-amber-600/50 bg-amber-950/20 p-2 text-amber-200">
                                    <strong>{previousVersionCleanupPreview.protectedBacktestsCount}</strong> backtest
                                    {previousVersionCleanupPreview.protectedBacktestsCount === 1 ? ' is' : 's are'} protected and will
                                    remain: {previousVersionCleanupPreview.openPositionBacktestsCount} referenced by open positions and{' '}
                                    {previousVersionCleanupPreview.referencedSourceBacktestsCount} used as provenance sources.
                                </p>
                            )}
                            <p className="text-xs text-zinc-500">
                                The current version ({formatStrategyVersionKey(previousVersionCleanupPreview.currentStrategyVersionKey)})
                                and its population jobs will not be changed. This deletion cannot be undone.
                            </p>
                        </div>
                    ) : (
                        'Loading cleanup preview…'
                    )
                }
                title="Delete previous-version backtests"
                onCancel={() => {
                    setIsPreviousVersionCleanupOpen(false);
                    setPreviousVersionCleanupPreview(null);
                }}
                onConfirm={() => {
                    void handleConfirmPreviousVersionCleanup();
                }}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Reset backtests"
                confirmVariant="danger"
                isConfirmLoading={resetStrategyBacktestsMutation.isPending}
                isOpen={isDeleteBacktestsOpen}
                message={
                    <div className="space-y-2 text-sm">
                        <p>
                            {deletePreview ? (
                                <>
                                    This will delete <strong>{deletePreview.toDeleteCount}</strong> stored backtest
                                    {deletePreview.toDeleteCount === 1 ? '' : 's'} for this strategy
                                    {selectedEntityType ? ` and entity type ${selectedEntityType}` : ''}.
                                </>
                            ) : (
                                <>
                                    This will delete eligible stored backtests for this strategy
                                    {selectedEntityType ? ` and entity type ${selectedEntityType}` : ''}.
                                </>
                            )}
                        </p>
                        {deletePreview && deletePreview.retainedBacktestsCount > 0 && (
                            <div className="border border-amber-500/60 rounded p-2 text-amber-200 space-y-1">
                                <p>
                                    <strong>{deletePreview.retainedBacktestsCount}</strong> protected backtests will remain, including
                                    research evidence, acceptance fixtures, quarantined results and open-position sources. Their provenance
                                    links will remain intact.
                                </p>
                                <p className="text-xs">Sample retained IDs: {deletePreview.sampleRetainedBacktestIds.join(', ')}.</p>
                            </div>
                        )}
                        <div className="border-t border-zinc-600 pt-3 space-y-2">
                            <FormCheckbox
                                checked={shouldCancelQueueJobsOnReset}
                                id="cancelQueueJobsOnReset"
                                label="Cancel queued jobs for this strategy"
                                onChange={(e) => setShouldCancelQueueJobsOnReset(e.target.checked)}
                            />
                            <FormCheckbox
                                checked={shouldPurgeQueueHistoryOnReset}
                                helpText="Deletes completed/failed/cancelled queue rows for this strategy. Optional; mainly reduces history/noise."
                                id="purgeQueueHistoryOnReset"
                                label="Purge queue history (completed/failed/cancelled)"
                                onChange={(e) => setShouldPurgeQueueHistoryOnReset(e.target.checked)}
                            />
                            <FormCheckbox
                                checked={shouldRepopulateOnReset}
                                id="repopulateOnReset"
                                label="Repopulate after reset (enqueue jobs)"
                                onChange={(e) => setShouldRepopulateOnReset(e.target.checked)}
                            />
                            {shouldRepopulateOnReset && (
                                <div className="space-y-2">
                                    <PopulationPriorityInput
                                        id="populationPriorityReset"
                                        priority={populationPriority}
                                        onChange={setPopulationPriority}
                                    />
                                    <FormCheckbox
                                        checked={bypassCache}
                                        helpText="Forces fresh entry calculation. Enable this after making changes to strategy entry logic to avoid using stale cached entries."
                                        id="bypassCacheReset"
                                        label="Bypass entry cache"
                                        onChange={(e) => setBypassCache(e.target.checked)}
                                    />
                                </div>
                            )}
                        </div>
                        <p className="text-red-400">
                            This action cannot be undone. Only strategy-level backtests are deleted; portfolio backtest artifacts are
                            preserved.
                        </p>
                    </div>
                }
                title="Reset backtests for strategy"
                onCancel={() => {
                    setIsDeleteBacktestsOpen(false);
                    setDeletePreview(null);
                }}
                onConfirm={handleConfirmResetBacktests}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Submit entity jobs"
                confirmVariant="primary"
                isConfirmLoading={triggerPopulateMutation.isPending}
                isOpen={entityToPopulate !== null}
                message={
                    entityToPopulate && (
                        <div className="space-y-3 text-sm">
                            <p>
                                This will submit <strong>{entityPopulationPreview?.estimatedJobCount ?? 0}</strong> previewed job
                                {(entityPopulationPreview?.estimatedJobCount ?? 0) === 1 ? '' : 's'} for entity{' '}
                                <span className="font-mono">
                                    {entityToPopulate.symbol} ({entityToPopulate.id}-{entityToPopulate.type})
                                </span>{' '}
                                under this strategy.
                            </p>
                            <div className="space-y-3 border-t border-zinc-600 pt-3">
                                <PopulationPriorityInput
                                    id="populationPriorityEntity"
                                    priority={populationPriority}
                                    onChange={setPopulationPriority}
                                />
                                <FormCheckbox
                                    checked={bypassCache}
                                    helpText="Forces fresh entry calculation. Enable this after making changes to strategy entry logic."
                                    id="bypassCacheEntity"
                                    label="Bypass entry cache"
                                    onChange={(e) => setBypassCache(e.target.checked)}
                                />
                            </div>
                            <p className="text-red-400">This may enqueue multiple backtests for this entity.</p>
                        </div>
                    )
                }
                title="Populate backtests for entity"
                onCancel={() => {
                    setEntityToPopulate(null);
                    setEntityPopulationPreview(null);
                }}
                onConfirm={() => {
                    if (entityToPopulate) {
                        void handleTriggerPopulateForEntity(entityToPopulate.id, entityToPopulate.type);
                    }

                    setEntityToPopulate(null);
                    setEntityPopulationPreview(null);
                }}
            />
        </div>
    );
};

export default StrategyBacktestOperations;
